"use client";

import { ApiErrorState } from "@/components/error/api-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { toVisaSearchQueryArgs } from "@/feature/visa/search/lib/visa-formatter-helper";
import { useMemo } from "react";

import { IVisaSearchSchema } from "@/feature/visa/search/types/visa.types";
import { useSearchVisaQuery } from "../api/visa-search-api";
import VisaListCard from "./results/visa-list-card";

type VisaSearchProps = {
  decoded: IVisaSearchSchema;
};

export default function VisaSearch({ decoded }: VisaSearchProps) {
  const searchQueryArgs = useMemo(
    () => toVisaSearchQueryArgs(decoded),
    [decoded],
  );
  const { data, error, isLoading, isFetching, isError, refetch } =
    useSearchVisaQuery(searchQueryArgs);
  const visas = data?.data ?? [];

  if (isError) {
    return (
      <ApiErrorState
        error={error}
        title="Error fetching visa data"
        onRetry={() => void refetch()}
        className="container min-h-[50vh] py-12"
      />
    );
  }

  return (
    <section className="container-wrapper py-4">
      <div className="container px-0">
        {isLoading || isFetching ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="rounded-2xl border bg-background p-4">
                <div className="grid gap-3 md:grid-cols-[280px_minmax(0,1fr)]">
                  <Skeleton className="h-44 w-full rounded-xl" />
                  <div className="space-y-3">
                    <Skeleton className="h-6 w-2/3" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-4/5" />
                    <div className="flex gap-2">
                      <Skeleton className="h-6 w-28" />
                      <Skeleton className="h-6 w-36" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : visas.length === 0 ? (
          <div className="rounded-xl border bg-background p-8 text-center">
            <h3 className="text-base font-semibold">No visas found</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Try searching with another country or visa type.
            </p>
          </div>
        ) : (
          <div className="space-y-4 ">
            {visas.map((visa, index) => (
              <VisaListCard
                key={visa.id}
                visa={visa}
                searchData={decoded}
                eagerImage={index === 0}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
